/**
 * The Bug Hunt's client state.
 *
 * A projection of what the server says, never a second source of truth. Three rules follow from
 * that, and they are what keep the game honest:
 *
 * 1. **The server decides, the scene reacts.** Nothing here concludes that a bug is fixed because
 *    an animation finished. `BugCompleted` arrives because a task's status changed in a committed
 *    transaction, and only then does the bat swing.
 *
 * 2. **Every message carries the resulting state.** A client that missed the previous event still
 *    converges from this one, which is what makes reconnection work without replaying history.
 *
 * 3. **Presentation never persists.** Positions, animation phases and floating messages live here
 *    and nowhere else. A refresh rebuilds them from `/bug-hunt/state`; the server has never heard
 *    of them.
 *
 * The animation queue exists because §35 is a real problem: `test.completed` and `work.completed`
 * can arrive milliseconds apart, and playing both at once produces a developer swinging a bat at a
 * bug that is still in the test zone. Transitions are therefore queued — and dropped when they
 * fall behind, because converging on the truth beats finishing a backlog of animations nobody is
 * waiting for.
 */

import type { ActivityState, Bug, BugHuntDeveloper, BugHuntEvent, BugHuntState, BugState, Priority } from '../lib/types'

export type AnimationPhase =
  | 'idle'
  | 'releasing'
  | 'hunting'
  | 'testing'
  | 'blocked'
  | 'captured'
  | 'defeating'
  | 'returning'

export interface FloatingMessage {
  id: number
  text: string
  /** Seconds remaining. Counted down by the renderer, removed at zero. */
  life: number
  tone: 'neutral' | 'ok' | 'warn' | 'danger'
}

export interface DeveloperEntity {
  id: string
  name: string
  avatarColor: string | null
  avatar: BugHuntDeveloper['avatar']
  teamId: string | null
  teamName: string | null
  /** Every team and squad they are in, main team first. */
  teams: NonNullable<BugHuntDeveloper['teams']>
  state: ActivityState
  sessions: BugHuntDeveloper['activeSessions']
  assignedBugCount: number
  /** An agent with a character of its own, drawn and labelled as an agent. */
  isAgent: boolean
  /** The line they put under their own name, when they wrote one. */
  statusPhrase: string | null
  /** How their AI agents look (docs/refactor/25), as JSON; null is random. */
  agentLooks: string | null

  /** Layout slot, assigned by the renderer and stable while the developer is on screen. */
  slot: number
  x: number
  y: number

  /** Clones split off from the body when a second agent starts. See `spawnAt`. */
  cloneCount: number
  /** Timestamp a clone appeared, so the split animation can play once. */
  spawnAt: number

  messages: FloatingMessage[]
  /** Swing animation progress, 0 to 1. Driven by the renderer. */
  swing: number
  /** True while the finishing move is playing. */
  finishing: boolean
}

export interface BugEntity {
  taskId: string
  externalId: string
  title: string
  priority: Priority
  state: BugState
  progressPercent: number
  developerId: string | null
  workSessionId: string | null
  phase: AnimationPhase
  /** Scene position. Interpolated by the renderer towards its target. */
  x: number
  y: number
  vx: number
  vy: number
  /** Set when the bug is flying off-screen after a defeat. */
  launchedAt: number
  /** Wing-beat phase, so a hundred bugs do not beat in unison. */
  seed: number
  testsPassed: number
  testsTotal: number
  blockerReason: string | null
  reopenCount: number
  isAgentDiscovered: boolean
  /** Server-computed elapsed time for the live session, or null when there is none. */
  elapsedSeconds: number | null
  /** The PRD or SDD this bug was raised against. */
  specificationId: string | null
  specificationKey: string | null
  specificationTitle: string | null
  /** Who is testing the fix, while somebody is: the bug sits at their station. */
  testerDeveloperId: string | null
  testerName: string | null
  commentCount: number
}

export interface Banner {
  id: number
  externalId: string
  text: string
  kind: 'fixed' | 'review' | 'reopened'
  life: number
}

export interface GameState {
  developers: Map<string, DeveloperEntity>
  bugs: Map<string, BugEntity>
  counts: BugHuntState['counts']
  completionPolicy: BugHuntState['completionPolicy']
  banners: Banner[]
  selectedDeveloperId: string | null
  selectedBugId: string | null
  /** Sanitised event lines, newest first, for the live log panel. */
  log: Array<{ id: number; at: string; text: string; developerId: string | null }>
}

/** Maximum simultaneous floating messages per developer, per §7 of the Bug Hunt brief. */
const MAX_MESSAGES = 3

/** Floating messages live this long, in seconds. */
const MESSAGE_LIFE = 4.5

const MAX_LOG = 120

/** If more than this many transitions are queued, intermediate ones are dropped. */
const QUEUE_LIMIT = 12

/** How long the racket swing lasts, in milliseconds. A little longer than the CSS animation. */
const SWING_MS = 1500

let nextId = 1

export class BugHuntStore {
  private state: GameState = {
    developers: new Map(),
    bugs: new Map(),
    counts: {
      total: 0, waiting: 0, assigned: 0, inProgress: 0, testing: 0,
      blocked: 0, readyForReview: 0, completedToday: 0, reopened: 0,
    },
    completionPolicy: 'ReadyForReview',
    banners: [],
    selectedDeveloperId: null,
    selectedBugId: null,
    log: [],
  }

  private listeners = new Set<() => void>()
  private queue: BugHuntEvent[] = []
  private snapshotCache: GameState | null = null

  /** Entities an event changed, to be replaced with copies before the next emit. */
  private touchedBugs = new Set<string>()
  private touchedDevelopers = new Set<string>()

  /** Swings in progress, by developer, so a new one restarts rather than overlaps. */
  private swings = new Map<string, ReturnType<typeof setTimeout>>()

  /**
   * Whether an event is about something on screen: a bug or a person this store holds.
   *
   * Looking at one team, the other teams' events still arrive — the stream is per organization —
   * and without this their banners and log lines would appear over a room they are not in.
   */
  concerns(event: BugHuntEvent): boolean {
    return this.state.bugs.has(event.taskId) || (event.developerId != null && this.state.developers.has(event.developerId))
  }

  /** Replaces everything from an authoritative read. Used on load and on reconnect. */
  hydrate(payload: BugHuntState): void {
    const developers = new Map<string, DeveloperEntity>()

    payload.developers.forEach((developer, index) => {
      const existing = this.state.developers.get(developer.developerId)

      developers.set(developer.developerId, {
        id: developer.developerId,
        name: developer.displayName,
        avatarColor: developer.avatarColor,
        avatar: developer.avatar,
        teamId: developer.teamId,
        teamName: developer.teamName,
        teams: developer.teams ?? [],
        state: developer.state,
        isAgent: developer.isAgent ?? false,
        statusPhrase: developer.statusPhrase ?? null,
        agentLooks: developer.agentLooks ?? null,
        sessions: developer.activeSessions,
        assignedBugCount: developer.assignedBugCount,
        slot: index,
        // Positions are kept across a hydrate so a reconnect does not teleport everyone.
        x: existing?.x ?? 0,
        y: existing?.y ?? 0,
        cloneCount: Math.max(0, developer.activeSessions.length - 1),
        spawnAt: existing?.spawnAt ?? 0,
        messages: existing?.messages ?? [],
        swing: 0,
        // A transition triggers a refetch that lands mid-swing; resetting here cut every swing off.
        finishing: existing?.finishing ?? false,
      })
    })

    const bugs = new Map<string, BugEntity>()
    for (const bug of [...payload.activeBugs, ...payload.waitingBugs]) {
      bugs.set(bug.taskId, this.toEntity(bug, this.state.bugs.get(bug.taskId)))
    }

    this.state = {
      ...this.state,
      developers,
      bugs,
      counts: payload.counts,
      completionPolicy: payload.completionPolicy,
    }

    this.emit()
  }

  private toEntity(bug: Bug, existing?: BugEntity): BugEntity {
    return {
      taskId: bug.taskId,
      externalId: bug.externalId,
      title: bug.title,
      priority: bug.priority,
      state: bug.state,
      progressPercent: bug.progressPercent,
      developerId: bug.developerId,
      workSessionId: bug.workSessionId,
      phase: phaseFor(bug.state),
      x: existing?.x ?? 0,
      y: existing?.y ?? 0,
      vx: existing?.vx ?? 0,
      vy: existing?.vy ?? 0,
      launchedAt: existing?.launchedAt ?? 0,
      seed: existing?.seed ?? Math.random() * Math.PI * 2,
      testsPassed: bug.testsPassed,
      testsTotal: bug.testsTotal,
      blockerReason: bug.blockerReason,
      reopenCount: bug.reopenCount,
      isAgentDiscovered: bug.isAgentDiscovered,
      elapsedSeconds: bug.elapsedSeconds,
      testerDeveloperId: bug.testerDeveloperId ?? null,
      testerName: bug.testerName ?? null,
      commentCount: bug.commentCount ?? 0,
      specificationId: bug.specificationId ?? null,
      specificationKey: bug.specificationKey ?? null,
      specificationTitle: bug.specificationTitle ?? null,
    }
  }

  /**
   * Accepts a batch of real-time events.
   *
   * Queued rather than applied immediately so that two transitions arriving in the same frame do
   * not fight over the same entity. When the queue grows past its limit the intermediate entries
   * are discarded and only the newest per bug is kept: falling behind is acceptable, showing a
   * state that is no longer true is not.
   */
  enqueue(events: BugHuntEvent[]): void {
    this.queue.push(...events)

    if (this.queue.length > QUEUE_LIMIT) {
      const newestPerBug = new Map<string, BugHuntEvent>()
      for (const event of this.queue) newestPerBug.set(event.taskId, event)
      this.queue = [...newestPerBug.values()]
    }

    this.drain()
  }

  private drain(): void {
    if (this.queue.length === 0) return

    const events = this.queue
    this.queue = []

    for (const event of events) this.apply(event)
    this.commit()
    this.emit()
  }

  /**
   * Replaces every entity an event changed with a copy.
   *
   * The scene's components are memoised on their props, so an entity changed in place looks
   * unchanged to them and is not redrawn until the next full reload of the state — which is why a
   * bug's progress or a developer's swing used to appear only after a refetch, or not at all.
   */
  private commit(): void {
    if (this.touchedBugs.size > 0) {
      const bugs = new Map(this.state.bugs)
      for (const id of this.touchedBugs) {
        const bug = bugs.get(id)
        if (bug) bugs.set(id, { ...bug })
      }
      this.state = { ...this.state, bugs }
      this.touchedBugs.clear()
    }

    if (this.touchedDevelopers.size > 0) {
      const developers = new Map(this.state.developers)
      for (const id of this.touchedDevelopers) {
        const developer = developers.get(id)
        if (developer) developers.set(id, { ...developer })
      }
      this.state = { ...this.state, developers }
      this.touchedDevelopers.clear()
    }
  }

  /**
   * The racket swing: when an agent finishes a bug, whether it goes to review or straight to fixed.
   *
   * Drawn for a fixed time and then undone, so the next finish swings again; while it lasts the
   * person stands with the racket even though their session has just ended.
   */
  private swing(developerId: string | null): void {
    if (!developerId) return
    const developer = this.state.developers.get(developerId)
    if (!developer) return

    developer.finishing = true
    this.touchedDevelopers.add(developerId)

    const running = this.swings.get(developerId)
    if (running) clearTimeout(running)

    this.swings.set(
      developerId,
      setTimeout(() => {
        this.swings.delete(developerId)
        const current = this.state.developers.get(developerId)
        if (!current) return
        current.finishing = false
        this.touchedDevelopers.add(developerId)
        this.commit()
        this.emit()
      }, SWING_MS),
    )
  }

  private apply(event: BugHuntEvent): void {
    const bug = this.state.bugs.get(event.taskId)
    if (bug) this.touchedBugs.add(event.taskId)
    if (event.developerId) this.touchedDevelopers.add(event.developerId)

    if (bug) {
      bug.state = event.state
      bug.progressPercent = event.progressPercent
      bug.developerId = event.developerId ?? bug.developerId
      bug.workSessionId = event.workSessionId ?? bug.workSessionId
      bug.phase = phaseFor(event.state)
    }

    if (event.message && event.developerId) {
      this.pushMessage(event.developerId, event.message, toneFor(event.kind))
    }

    this.log(event)

    switch (event.kind) {
      case 'BugStarted':
        if (bug) bug.phase = 'releasing'
        break

      case 'BugReopened':
        if (bug) {
          bug.phase = 'returning'
          bug.reopenCount += 1
        }
        this.banner(event.externalId, 'reopened')
        break

      case 'BugTestsFailed':
        if (bug) bug.phase = 'hunting'
        break

      case 'BugReadyForReview':
        // The agent is done: a swing, then the glass comes down over the stunned mosquito.
        if (bug) bug.phase = 'captured'
        this.swing(event.developerId)
        this.banner(event.externalId, 'review')
        break

      case 'BugCompleted':
        if (bug) {
          bug.phase = 'defeating'
          bug.launchedAt = performance.now()
        }
        this.swing(event.developerId)
        this.banner(event.externalId, 'fixed')
        break

      default:
        break
    }
  }

  private banner(externalId: string, kind: Banner['kind']): void {
    this.state.banners = [
      ...this.state.banners.slice(-2),
      { id: nextId++, externalId, text: externalId, kind, life: 3 },
    ]
  }

  private pushMessage(developerId: string, text: string, tone: FloatingMessage['tone']): void {
    const developer = this.state.developers.get(developerId)
    if (!developer) return

    // Capped and oldest-dropped. A developer with eight overlapping labels above their head is
    // less informative than one with three, not more.
    developer.messages = [
      ...developer.messages.slice(-(MAX_MESSAGES - 1)),
      { id: nextId++, text, life: MESSAGE_LIFE, tone },
    ]
  }

  private log(event: BugHuntEvent): void {
    if (!event.message) return

    this.state.log = [
      { id: nextId++, at: event.occurredAt, text: event.message, developerId: event.developerId },
      ...this.state.log,
    ].slice(0, MAX_LOG)
  }

  /** Advances time-based state. Called by the renderer once per frame. */
  tick(deltaSeconds: number): void {
    let changed = false

    for (const developer of this.state.developers.values()) {
      if (developer.messages.length === 0) continue

      const remaining = developer.messages
        .map((message) => ({ ...message, life: message.life - deltaSeconds }))
        .filter((message) => message.life > 0)

      if (remaining.length !== developer.messages.length) changed = true
      developer.messages = remaining
    }

    if (this.state.banners.length > 0) {
      const banners = this.state.banners
        .map((banner) => ({ ...banner, life: banner.life - deltaSeconds }))
        .filter((banner) => banner.life > 0)

      if (banners.length !== this.state.banners.length) changed = true
      this.state.banners = banners
    }

    if (changed) this.emit()
  }

  select(developerId: string | null, bugId: string | null): void {
    this.state.selectedDeveloperId = developerId
    this.state.selectedBugId = bugId
    this.emit()
  }

  /**
   * The live state, for the renderer.
   *
   * Returns the mutable object on purpose: the renderer reads it sixty times a second and copying
   * a map of entities per frame would be pure waste. React reads `snapshot()` instead.
   */
  get live(): GameState {
    return this.state
  }

  /**
   * An identity-stable snapshot for React.
   *
   * `useSyncExternalStore` compares by reference, so this must return the same object until
   * something actually changed — otherwise every frame would be a re-render, which is exactly the
   * 60 FPS React tree ADR-0008 and §32 both rule out.
   */
  snapshot = (): GameState => {
    if (!this.snapshotCache) this.snapshotCache = { ...this.state }
    return this.snapshotCache
  }

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  private emit(): void {
    this.snapshotCache = { ...this.state }
    this.listeners.forEach((listener) => listener())
  }
}

function phaseFor(state: BugState): AnimationPhase {
  switch (state) {
    case 'InProgress': return 'hunting'
    case 'Testing':
    case 'InTesting': return 'testing'
    case 'Blocked': return 'blocked'
    case 'ReadyForReview': return 'captured'
    case 'Completed': return 'defeating'
    case 'Reopened': return 'returning'
    default: return 'idle'
  }
}

function toneFor(kind: BugHuntEvent['kind']): FloatingMessage['tone'] {
  switch (kind) {
    case 'BugBlocked': return 'danger'
    case 'BugTestsFailed': return 'warn'
    case 'BugTestsPassed':
    case 'BugCompleted': return 'ok'
    default: return 'neutral'
  }
}
